import type { ComponentType } from 'react';
import {
  Award,
  Bell,
  Bookmark,
  BookOpen,
  Calendar,
  ClipboardList,
  FileText,
  GraduationCap,
  Library,
  Lock,
  ScrollText,
  Users,
} from 'lucide-react';
import { ROUTES } from 'constants/routes';
import { learnerNav } from 'constants/copy';
import { isSchoolLearnPath, type LearnerNavLinkKey } from 'utils/learnerNavActive';

/** Routes covered by the mobile bottom navigation - excluded from the hamburger drawer. */
export const MOBILE_BOTTOM_NAV_PATHS = [
  ROUTES.DASHBOARD,
  ...[
    ROUTES.PARASHARI_LEARN,
    ROUTES.JAIMINI_LEARN,
    ROUTES.BNN_LEARN,
    ROUTES.KP_LEARN,
    ROUTES.LAL_KITAB_LEARN,
    ROUTES.NUMEROLOGY_LEARN,
    ROUTES.SURYA_SIDDHANTA_LEARN,
    ROUTES.PANCHANG_LEARN,
    ROUTES.VASTU_LEARN,
    ROUTES.SWARA_VIGYAN_LEARN,
    ROUTES.YANTRA_VIDYA_LEARN,
    ROUTES.TANTRA_LEARN,
  ],
  ROUTES.AI_TUTOR,
  ROUTES.PROFILE,
  ROUTES.SETTINGS,
] as const;

export function isMobileBottomNavDestination(to: string): boolean {
  const path = to.split('?')[0];

  if (path === ROUTES.DASHBOARD) return true;
  if (path === ROUTES.AI_TUTOR || path.startsWith('/ai')) return true;
  if (path === ROUTES.PROFILE || path === ROUTES.SETTINGS) return true;
  if (isSchoolLearnPath(path) || path === ROUTES.SADHANA) return true;
  return false;
}

export type SidebarLink = {
  label: string;
  to: string;
  icon: ComponentType<{ className?: string }>;
  navigateState?: Record<string, unknown>;
  navKey?: LearnerNavLinkKey;
  /** Group heading. Consecutive links sharing a section render under one heading. */
  section?: string;
};

type LearnerNavItem = SidebarLink & {
  /** Backed by `inst_*` data - empty without an active institute batch enrollment. */
  institutionOnly?: boolean;
};

/**
 * Learner sidebar + mobile drawer destinations, grouped for scannability.
 *
 * Deliberately excludes AskGuru (top nav, bottom tab, floating chat), Progress
 * and Settings (avatar menu, bottom tab) so each destination has one home.
 */
const LEARNER_NAV_ITEMS: LearnerNavItem[] = [
  {
    section: learnerNav.sectionLearn,
    label: learnerNav.courses,
    to: ROUTES.SCHOOLS,
    icon: BookOpen,
    navKey: 'schools',
  },
  {
    section: learnerNav.sectionLearn,
    label: learnerNav.programs,
    to: ROUTES.PROGRAMS,
    icon: Library,
  },
  {
    section: learnerNav.sectionLearn,
    label: learnerNav.batches,
    to: ROUTES.BATCHES,
    icon: GraduationCap,
    institutionOnly: true,
  },
  {
    section: learnerNav.sectionLearn,
    label: learnerNav.calendar,
    to: ROUTES.CALENDAR,
    icon: Calendar,
    institutionOnly: true,
  },

  {
    section: learnerNav.sectionPractice,
    label: learnerNav.assessments,
    to: ROUTES.ASSESSMENTS,
    icon: ClipboardList,
  },
  {
    section: learnerNav.sectionPractice,
    label: learnerNav.sadhana,
    to: ROUTES.SADHANA,
    icon: ScrollText,
  },
  {
    section: learnerNav.sectionPractice,
    label: learnerNav.certificates,
    to: ROUTES.CERTIFICATES,
    icon: Award,
  },

  {
    section: learnerNav.sectionLibrary,
    label: learnerNav.notes,
    to: ROUTES.NOTES,
    icon: FileText,
  },
  {
    section: learnerNav.sectionLibrary,
    label: learnerNav.privateNotes,
    to: ROUTES.PRIVATE_NOTES,
    icon: Lock,
  },
  {
    section: learnerNav.sectionLibrary,
    label: learnerNav.bookmarks,
    to: ROUTES.BOOKMARKS,
    icon: Bookmark,
  },
  {
    section: learnerNav.sectionLibrary,
    label: learnerNav.studyMaterials,
    to: ROUTES.STUDY_MATERIALS,
    icon: ScrollText,
    institutionOnly: true,
  },

  {
    section: learnerNav.sectionCommunity,
    label: learnerNav.community,
    to: ROUTES.COMMUNITY,
    icon: Users,
  },
  {
    section: learnerNav.sectionCommunity,
    label: learnerNav.reminders,
    to: ROUTES.REMINDERS,
    icon: Bell,
  },
];

/**
 * `hasInstitute` should reflect an active batch enrollment (`hasApprovedMembership`),
 * not soft-home affiliation - soft-home alone leaves the institute pages empty.
 */
export function buildLearnerNavLinks(hasInstitute: boolean): SidebarLink[] {
  return LEARNER_NAV_ITEMS.filter((item) => !item.institutionOnly || hasInstitute);
}
